// Bilingual blog content — French + English
// Each article has a shared `id` for cross-language navigation, plus a localized `slug`.
// Blocks support inline scientific links via arrays of { link, href } + strings.
//
// Structure per article: { id, slug, title, excerpt, category, readMin, cover, blocks[], published }
// Block types: "p" (paragraph, string or array of parts), "h" (h2 subheading), "ul" (bullet list)

// Cover images (Unsplash, free to use)
const COVERS = {
  reprise: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1200&q=80",
  frequence: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=1200&q=80",
  cardio: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1200&q=80",
  nutrition: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1200&q=80",
  motivation: "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1200&q=80",
};

// Scientific reference URLs (peer-reviewed or authoritative sources)
const REF = {
  bull2020: "https://bjsm.bmj.com/content/54/24/1451",
  garber2011: "https://pubmed.ncbi.nlm.nih.gov/21694556/",
  cheung2003: "https://pubmed.ncbi.nlm.nih.gov/12617692/",
  schoenfeld2019: "https://pubmed.ncbi.nlm.nih.gov/30153194/",
  lopez2022: "https://pubmed.ncbi.nlm.nih.gov/35441647/",
  bellicha2021: "https://pubmed.ncbi.nlm.nih.gov/33690996/",
  thomas2016: "https://pubmed.ncbi.nlm.nih.gov/26891166/",
  kerksick2017: "https://pubmed.ncbi.nlm.nih.gov/28919842/",
  sawka2007: "https://pubmed.ncbi.nlm.nih.gov/17277604/",
  teixeira2012: "https://pubmed.ncbi.nlm.nih.gov/22726453/",
  silva2018: "https://scholar.google.com/scholar?q=Silva+2018+implementation+intentions+physical+activity",
  ma2023: "https://scholar.google.com/scholar?q=Ma+2023+physical+activity+habits+meta-analysis",
};

// ------- FRENCH ARTICLES -------
const ARTICLES_FR = [
  {
    id: "return-to-exercise",
    slug: "comment-reprendre-le-sport",
    title: "Comment reprendre le sport après une longue période d'arrêt ?",
    excerpt: "Découvrez comment reprendre progressivement une activité physique, éviter d'en faire trop dès le départ et construire une routine durable.",
    category: "Reprise",
    readMin: 5,
    cover: COVERS.reprise,
    blocks: [
      { type: "p", value: "Reprendre une activité physique après plusieurs mois ou plusieurs années d'arrêt peut sembler difficile. Pourtant, il n'est pas nécessaire d'être déjà en bonne condition physique pour recommencer." },
      { type: "p", value: "Le plus important n'est pas de réaliser une séance parfaite, mais de retrouver progressivement l'habitude de bouger." },
      { type: "h", value: "Commencez progressivement" },
      { type: "p", value: "L'une des erreurs les plus fréquentes consiste à vouloir reprendre directement au niveau que l'on avait avant son arrêt. Le corps a besoin de temps pour se réhabituer aux efforts, aux impacts et au renforcement musculaire." },
      { type: "p", value: "Pour vos premières semaines, choisissez des activités simples :" },
      { type: "ul", value: [
        "marche active ;",
        "vélo à intensité modérée ;",
        "renforcement musculaire au poids du corps ;",
        "exercices de mobilité ;",
        "séances courtes en circuit.",
      ]},
      { type: "p", value: "Une séance de 20 à 30 minutes peut déjà constituer un très bon point de départ. La durée et l'intensité pourront ensuite augmenter progressivement selon vos sensations." },
      { type: "p", value: "Les recommandations de l'Organisation mondiale de la santé rappellent que toute activité physique est préférable à l'inactivité. Il n'est donc pas nécessaire d'atteindre immédiatement un volume élevé d'entraînement pour commencer à obtenir des bénéfices." },
      { type: "p", value: [
        "Selon ",
        { link: "Bull et al. (2020)", href: REF.bull2020 },
        ", les personnes inactives devraient commencer par de petites quantités d'activité physique avant d'augmenter progressivement la fréquence, l'intensité et la durée.",
      ]},
      { type: "p", value: [
        "L'American College of Sports Medicine recommande également d'adapter le programme au niveau habituel d'activité, à la condition physique, à l'état de santé et aux objectifs de chaque personne. ",
        { link: "Garber et al. (2011)", href: REF.garber2011 },
        " insistent notamment sur l'importance d'une progression individualisée.",
      ]},
      { type: "h", value: "Fixez-vous un premier objectif réaliste" },
      { type: "p", value: "Votre premier objectif ne doit pas forcément être de perdre plusieurs kilos ou de courir pendant une heure." },
      { type: "p", value: "Il peut simplement être de :" },
      { type: "ul", value: [
        "réaliser deux séances par semaine ;",
        "marcher davantage au quotidien ;",
        "terminer une séance sans vous sentir complètement épuisé ;",
        "maintenir votre routine pendant un mois.",
      ]},
      { type: "p", value: "Un objectif réalisable permet de construire une première réussite et de renforcer progressivement la confiance." },
      { type: "h", value: "Différenciez les courbatures et la douleur" },
      { type: "p", value: "Après une activité inhabituelle, des courbatures peuvent apparaître dans les heures qui suivent. Elles sont souvent plus importantes un à deux jours après la séance, notamment après des exercices nouveaux ou comprenant une part importante de contractions excentriques." },
      { type: "p", value: [
        "Ce phénomène, appelé douleur musculaire d'apparition retardée, est décrit par ",
        { link: "Cheung, Hume et Maxwell (2003)", href: REF.cheung2003 },
        ".",
      ]},
      { type: "p", value: "Les courbatures ne sont cependant pas indispensables pour qu'une séance soit efficace." },
      { type: "p", value: "Une douleur vive, localisée, inhabituelle ou persistante ne doit pas être considérée comme une simple courbature. Arrêtez l'exercice concerné et demandez l'avis d'un professionnel de santé si la douleur persiste." },
      { type: "h", value: "Choisissez une activité qui vous plaît" },
      { type: "p", value: "La meilleure activité n'est pas forcément celle qui brûle le plus de calories. C'est surtout celle que vous pourrez pratiquer régulièrement." },
      { type: "p", value: "Vous pouvez choisir la musculation, la marche, la natation, le vélo, les cours collectifs ou l'entraînement en extérieur." },
      { type: "p", value: "Le plaisir et la facilité d'organisation sont essentiels pour maintenir une activité sur la durée." },
      { type: "h", value: "À retenir" },
      { type: "p", value: "Pour reprendre le sport durablement :" },
      { type: "ul", value: [
        "commencez avec une intensité adaptée ;",
        "privilégiez une ou deux séances régulières plutôt qu'une semaine excessive ;",
        "augmentez progressivement la durée et la difficulté ;",
        "laissez du temps à votre corps pour récupérer ;",
        "adaptez les exercices en cas de douleur ou de limitation ;",
        "choisissez une activité compatible avec votre quotidien.",
      ]},
      { type: "p", value: "L'objectif des premières semaines n'est pas de battre des records, mais de créer une routine que vous pourrez conserver." },
    ],
  },
  {
    id: "weekly-training-frequency",
    slug: "combien-de-seances-par-semaine",
    title: "Combien de fois faut-il faire du sport par semaine pour progresser ?",
    excerpt: "Découvrez combien de séances sont réellement nécessaires pour progresser et pourquoi la régularité compte davantage que l'entraînement quotidien.",
    category: "Méthode",
    readMin: 4,
    cover: COVERS.frequence,
    blocks: [
      { type: "p", value: "Lorsque l'on débute, il est fréquent de penser qu'il faut s'entraîner presque tous les jours pour obtenir des résultats." },
      { type: "p", value: "En réalité, la meilleure fréquence est celle qui permet de progresser tout en restant compatible avec votre emploi du temps et votre récupération." },
      { type: "h", value: "Deux séances par semaine constituent une bonne base" },
      { type: "p", value: "Pour une personne débutante, deux séances structurées par semaine peuvent déjà permettre d'améliorer sa force, son endurance et sa condition physique générale." },
      { type: "p", value: "Cette fréquence laisse également plusieurs jours de récupération entre les entraînements. Elle est généralement plus facile à maintenir qu'un programme comprenant cinq ou six séances hebdomadaires." },
      { type: "p", value: "Vous pouvez, par exemple, commencer par deux séances complètes associant :" },
      { type: "ul", value: [
        "des exercices pour les jambes ;",
        "des mouvements pour le haut du corps ;",
        "du gainage ;",
        "quelques minutes de travail cardiovasculaire.",
      ]},
      { type: "p", value: [
        "L'American College of Sports Medicine recommande de solliciter les principaux groupes musculaires environ deux à trois jours par semaine. Ces recommandations sont présentées dans le travail de ",
        { link: "Garber et al. (2011)", href: REF.garber2011 },
        ".",
      ]},
      { type: "p", value: "L'Organisation mondiale de la santé recommande également aux adultes de pratiquer des activités de renforcement musculaire au moins deux jours par semaine, en complément des activités cardiovasculaires." },
      { type: "p", value: [
        "Ces recommandations représentent néanmoins un objectif général. Une personne totalement inactive peut commencer avec un volume plus faible et progresser ensuite, comme l'expliquent ",
        { link: "Bull et al. (2020)", href: REF.bull2020 },
        ".",
      ]},
      { type: "h", value: "Trois séances permettent de varier davantage" },
      { type: "p", value: "Lorsque votre corps s'habitue à l'entraînement, vous pouvez passer à trois séances par semaine." },
      { type: "p", value: "Une organisation simple pourrait comprendre :" },
      { type: "ul", value: [
        "une séance de renforcement musculaire ;",
        "une séance cardiovasculaire ;",
        "une séance combinant renforcement, mobilité et cardio.",
      ]},
      { type: "p", value: "Vous pouvez également réaliser trois séances complètes plus courtes si cette organisation correspond mieux à votre emploi du temps." },
      { type: "h", value: "La fréquence n'est pas le seul élément important" },
      { type: "p", value: "Faire davantage de séances ne garantit pas automatiquement davantage de résultats." },
      { type: "p", value: "Le volume total d'entraînement, la difficulté des exercices, le nombre de séries, la récupération et la régularité sont également importants." },
      { type: "p", value: [
        "La méta-analyse de ",
        { link: "Schoenfeld, Grgic et Krieger (2019)", href: REF.schoenfeld2019 },
        " montre que lorsque le volume total d'entraînement est comparable, augmenter uniquement le nombre de séances ne produit pas nécessairement une différence importante sur le développement musculaire.",
      ]},
      { type: "p", value: "La fréquence sert donc principalement à répartir efficacement le travail de la semaine." },
      { type: "h", value: "N'oubliez pas la récupération" },
      { type: "p", value: "Les adaptations provoquées par l'entraînement se construisent également pendant les périodes de récupération." },
      { type: "p", value: "Un programme efficace doit donc inclure :" },
      { type: "ul", value: [
        "des jours plus légers ou sans entraînement intense ;",
        "un sommeil suffisant ;",
        "une alimentation adaptée ;",
        "une progression raisonnable ;",
        "une adaptation des séances en cas de fatigue inhabituelle.",
      ]},
      { type: "h", value: "À retenir" },
      { type: "p", value: "Pour une personne débutante :" },
      { type: "ul", value: [
        "une séance par semaine permet déjà de se remettre en mouvement ;",
        "deux séances constituent une base solide ;",
        "trois séances permettent de varier davantage les contenus ;",
        "il n'est pas obligatoire de s'entraîner tous les jours ;",
        "la régularité est plus importante qu'une fréquence impossible à maintenir.",
      ]},
      { type: "p", value: "Deux séances réalisées chaque semaine pendant plusieurs mois seront généralement plus utiles que cinq séances réalisées pendant seulement dix jours." },
    ],
  },
  {
    id: "cardio-strength-weight-loss",
    slug: "cardio-ou-musculation-pour-perdre-du-poids",
    title: "Faut-il faire du cardio ou de la musculation pour perdre du poids ?",
    excerpt: "Comprenez les rôles complémentaires du cardio et de la musculation dans la perte de masse grasse et l'amélioration de la composition corporelle.",
    category: "Perte de poids",
    readMin: 5,
    cover: COVERS.cardio,
    blocks: [
      { type: "p", value: "De nombreuses personnes pensent qu'il faut choisir entre le cardio et la musculation pour perdre du poids." },
      { type: "p", value: "En pratique, ces deux formes d'entraînement n'ont pas exactement le même rôle, mais elles peuvent être complémentaires." },
      { type: "h", value: "Le cardio augmente la dépense énergétique" },
      { type: "p", value: "La marche rapide, le vélo, la natation, la course ou les entraînements en circuit augmentent la dépense énergétique pendant l'activité." },
      { type: "p", value: "Il n'est pas obligatoire de courir." },
      { type: "p", value: "Pour une personne débutante, une marche active peut déjà constituer une option efficace, accessible et plus facile à répéter plusieurs fois par semaine." },
      { type: "p", value: "Vous pouvez commencer par des périodes de 20 à 30 minutes, puis augmenter progressivement la durée ou l'intensité." },
      { type: "h", value: "La musculation aide à préserver la masse musculaire" },
      { type: "p", value: "Lors d'une perte de poids, l'objectif ne devrait pas uniquement être de voir diminuer le chiffre affiché sur la balance." },
      { type: "p", value: "Il est également important de préserver autant que possible la masse musculaire." },
      { type: "p", value: "Le renforcement musculaire améliore la force et aide à maintenir ou à développer les tissus musculaires. Une séance peut être réalisée avec des machines, des haltères, des élastiques ou simplement le poids du corps." },
      { type: "p", value: [
        "La méta-analyse de ",
        { link: "Lopez et al. (2022)", href: REF.lopez2022 },
        " montre que l'entraînement en résistance peut améliorer la composition corporelle chez les personnes en surpoids ou en situation d'obésité, notamment en favorisant la diminution de la masse grasse et le maintien ou le développement de la masse maigre.",
      ]},
      { type: "h", value: "Pourquoi associer cardio et musculation ?" },
      { type: "p", value: "Une analyse regroupant douze revues systématiques et 149 études a montré que l'exercice physique pouvait améliorer le poids et la composition corporelle chez les adultes en surpoids ou en situation d'obésité." },
      { type: "p", value: [
        "Selon ",
        { link: "Bellicha et al. (2021)", href: REF.bellicha2021 },
        ", le travail aérobie est particulièrement utile pour augmenter la dépense énergétique et réduire la masse grasse.",
      ]},
      { type: "p", value: "Le renforcement musculaire apporte un intérêt complémentaire en contribuant au maintien de la masse maigre." },
      { type: "p", value: "Une semaine simple pourrait donc comprendre :" },
      { type: "ul", value: [
        "une ou deux séances de renforcement musculaire ;",
        "une séance de cardio structurée ;",
        "de la marche ou une activité légère les autres jours.",
      ]},
      { type: "h", value: "L'alimentation reste importante" },
      { type: "p", value: "L'activité physique contribue à la dépense énergétique, mais l'évolution du poids dépend également de l'alimentation, du sommeil, de la récupération et du niveau général d'activité quotidienne." },
      { type: "p", value: "Cela ne signifie pas qu'il faut suivre un régime extrêmement restrictif." },
      { type: "p", value: "Une approche progressive est généralement plus facile à maintenir :" },
      { type: "ul", value: [
        "consommer des portions adaptées ;",
        "inclure régulièrement des légumes et des fruits ;",
        "manger suffisamment de protéines ;",
        "limiter les boissons très caloriques ;",
        "éviter de compenser systématiquement chaque séance par une prise alimentaire excessive.",
      ]},
      { type: "h", value: "Ne vous fiez pas uniquement à la balance" },
      { type: "p", value: "Il est possible de perdre de la masse grasse sans observer immédiatement une forte diminution du poids, notamment lorsque la masse musculaire est préservée ou développée." },
      { type: "p", value: "Vous pouvez également suivre :" },
      { type: "ul", value: [
        "votre tour de taille ;",
        "votre aisance dans les vêtements ;",
        "votre force ;",
        "votre endurance ;",
        "votre niveau d'énergie ;",
        "des photographies prises dans des conditions similaires.",
      ]},
      { type: "h", value: "À retenir" },
      { type: "p", value: "Pour perdre de la masse grasse durablement :" },
      { type: "ul", value: [
        "le cardio augmente la dépense énergétique ;",
        "la musculation aide à préserver ou à développer les muscles ;",
        "l'association des deux permet un travail plus complet ;",
        "l'alimentation influence également l'évolution du poids ;",
        "la régularité est plus importante que les séances extrêmes.",
      ]},
      { type: "p", value: "Il n'est donc pas nécessaire de choisir définitivement entre le cardio et la musculation." },
    ],
  },
  {
    id: "nutrition-before-after-workout",
    slug: "que-manger-avant-et-apres-le-sport",
    title: "Que manger avant et après une séance de sport ?",
    excerpt: "Découvrez quoi manger avant et après une séance sans rendre la nutrition sportive inutilement compliquée.",
    category: "Nutrition",
    readMin: 6,
    cover: COVERS.nutrition,
    blocks: [
      { type: "p", value: "L'alimentation peut influencer votre confort pendant l'entraînement et votre récupération." },
      { type: "p", value: "Toutefois, une personne débutante n'a pas besoin de suivre un protocole nutritionnel compliqué." },
      { type: "p", value: "La priorité reste d'avoir une alimentation suffisamment variée et équilibrée sur l'ensemble de la journée." },
      { type: "h", value: "Que manger avant l'entraînement ?" },
      { type: "p", value: "Le repas pris avant la séance doit vous apporter de l'énergie sans provoquer d'inconfort digestif." },
      { type: "p", value: "Si vous mangez environ deux à trois heures avant l'entraînement, vous pouvez choisir un repas classique comprenant :" },
      { type: "ul", value: [
        "une source de glucides, comme du riz, du pain, des pâtes ou des pommes de terre ;",
        "une source de protéines, comme des œufs, du poisson, de la viande, un produit laitier ou des légumineuses ;",
        "des légumes ;",
        "de l'eau.",
      ]},
      { type: "p", value: "Si votre dernier repas est éloigné et que vous ressentez la faim, une petite collation peut suffire :" },
      { type: "ul", value: [
        "un fruit ;",
        "une compote ;",
        "un yaourt ;",
        "une tranche de pain ;",
        "une petite portion de céréales simples.",
      ]},
      { type: "p", value: "Évitez de tester un repas très copieux, très gras ou inhabituel juste avant une séance importante." },
      { type: "p", value: [
        "Le consensus publié par ",
        { link: "Thomas, Erdman et Burke (2016)", href: REF.thomas2016 },
        " explique que l'alimentation doit être adaptée au type d'effort, à sa durée, à son intensité et à la tolérance digestive de chaque personne.",
      ]},
      { type: "p", value: "Les glucides jouent notamment un rôle important pendant les efforts prolongés ou intenses." },
      { type: "p", value: "Les besoins d'un débutant réalisant une séance de remise en forme de 45 minutes restent cependant différents de ceux d'un sportif d'endurance." },
      { type: "h", value: "Que manger après la séance ?" },
      { type: "p", value: "Après l'entraînement, un repas équilibré aide à fournir au corps les nutriments nécessaires à la récupération." },
      { type: "p", value: "Il peut comprendre :" },
      { type: "ul", value: [
        "une source de protéines ;",
        "une source de glucides ;",
        "des légumes ou un fruit ;",
        "de l'eau.",
      ]},
      { type: "p", value: "Par exemple :" },
      { type: "ul", value: [
        "poulet, riz et légumes ;",
        "omelette, pain et salade ;",
        "poisson, pommes de terre et légumes ;",
        "yaourt, fruit et flocons d'avoine pour une collation.",
      ]},
      { type: "h", value: "Le shaker de protéines est-il obligatoire ?" },
      { type: "p", value: "Non." },
      { type: "p", value: "Un complément alimentaire n'est pas indispensable lorsque les besoins sont couverts par l'alimentation." },
      { type: "p", value: "Les protéines participent à la réparation et à l'adaptation musculaires, mais les résultats dépendent surtout de l'apport total quotidien et de sa répartition au cours de la journée." },
      { type: "p", value: [
        "Le consensus de ",
        { link: "Kerksick et al. (2017)", href: REF.kerksick2017 },
        " indique que la consommation de protéines autour de l'entraînement peut soutenir la récupération.",
      ]},
      { type: "p", value: "Il n'est toutefois pas nécessaire de paniquer si vous ne consommez pas un shaker immédiatement après votre dernière répétition." },
      { type: "p", value: "Pour une personne débutante, une alimentation variée contenant régulièrement des œufs, du poisson, de la viande, des produits laitiers, du soja ou des légumineuses peut généralement apporter les protéines nécessaires." },
      { type: "h", value: "N'oubliez pas l'hydratation" },
      { type: "p", value: "Les besoins en eau dépendent notamment :" },
      { type: "ul", value: [
        "de la durée de la séance ;",
        "de la température ;",
        "de la transpiration ;",
        "de l'intensité de l'effort ;",
        "des caractéristiques individuelles.",
      ]},
      { type: "p", value: "Pour une séance classique et relativement courte, l'eau constitue généralement un choix adapté." },
      { type: "p", value: "Lorsque l'effort devient plus long, qu'il fait très chaud ou que la transpiration est importante, les besoins en liquides et en électrolytes peuvent augmenter." },
      { type: "p", value: [
        "Les recommandations de ",
        { link: "Sawka et al. (2007)", href: REF.sawka2007 },
        " insistent sur l'importance d'individualiser l'hydratation en fonction des pertes liées à la transpiration et des conditions de pratique.",
      ]},
      { type: "p", value: "L'objectif n'est pas de boire le plus possible, mais de commencer la séance correctement hydraté et de remplacer raisonnablement les liquides perdus." },
      { type: "h", value: "À retenir" },
      { type: "p", value: "Avant et après une séance :" },
      { type: "ul", value: [
        "privilégiez des aliments simples que vous digérez bien ;",
        "adaptez la quantité au délai avant l'effort ;",
        "consommez régulièrement des protéines et des glucides ;",
        "hydratez-vous au cours de la journée ;",
        "ne rendez pas la nutrition plus compliquée qu'elle ne doit l'être ;",
        "ne considérez pas les compléments alimentaires comme obligatoires.",
      ]},
      { type: "p", value: "La qualité générale de votre alimentation est plus importante qu'un aliment consommé à une minute précise." },
    ],
  },
  {
    id: "exercise-motivation",
    slug: "comment-rester-motive-pour-le-sport",
    title: "Comment rester motivé quand on commence le sport ?",
    excerpt: "Découvrez des stratégies simples et appuyées par la science pour rester régulier, même lorsque la motivation diminue.",
    category: "Motivation",
    readMin: 5,
    cover: COVERS.motivation,
    blocks: [
      { type: "p", value: "La motivation est souvent élevée au début d'un programme." },
      { type: "p", value: "Elle peut cependant diminuer lorsque la fatigue apparaît, que l'emploi du temps se remplit ou que les résultats sont moins rapides que prévu." },
      { type: "p", value: "Pour pratiquer durablement, il est préférable de construire une organisation qui fonctionne même lorsque la motivation est moins forte." },
      { type: "h", value: "Choisissez un objectif concret" },
      { type: "p", value: "Un objectif comme « me remettre en forme » est positif, mais il reste difficile à mesurer." },
      { type: "p", value: "Transformez-le en une action précise :" },
      { type: "ul", value: [
        "réaliser deux séances par semaine ;",
        "marcher 30 minutes trois fois par semaine ;",
        "réussir dix pompes adaptées ;",
        "courir cinq kilomètres sans s'arrêter ;",
        "augmenter progressivement les charges utilisées ;",
        "retrouver davantage d'énergie au quotidien.",
      ]},
      { type: "p", value: "Un objectif concret permet de savoir exactement ce que vous cherchez à accomplir." },
      { type: "h", value: "Choisissez une activité qui a du sens pour vous" },
      { type: "p", value: "Certaines personnes font du sport uniquement parce qu'elles pensent qu'elles y sont obligées." },
      { type: "p", value: "Cette motivation peut fonctionner temporairement, mais elle est souvent difficile à maintenir." },
      { type: "p", value: [
        "La revue systématique de ",
        { link: "Teixeira et al. (2012)", href: REF.teixeira2012 },
        " montre que les formes de motivation les plus autonomes sont davantage associées à une pratique durable de l'activité physique.",
      ]},
      { type: "p", value: "Vous avez donc plus de chances de rester régulier lorsque vous comprenez pourquoi vous vous entraînez, que l'activité correspond à vos préférences et que vous disposez d'une certaine liberté dans la manière de la pratiquer." },
      { type: "h", value: "Planifiez précisément vos séances" },
      { type: "p", value: "Écrire « faire du sport cette semaine » reste trop vague." },
      { type: "p", value: "Il est plus efficace de prévoir quelque chose de précis :" },
      { type: "p", value: "Mardi à 18 heures, je réalise une séance de 30 minutes à la maison." },
      { type: "p", value: "Ce type de plan précise le moment, le lieu et l'action." },
      { type: "p", value: [
        "La méta-analyse de ",
        { link: "Silva et al. (2018)", href: REF.silva2018 },
        " montre que les plans d'action précis, parfois appelés intentions d'implémentation, peuvent aider à transformer une intention en comportement réel.",
      ]},
      { type: "h", value: "Préparez une solution pour les journées difficiles" },
      { type: "p", value: "Vous pouvez prévoir deux versions de chaque séance :" },
      { type: "ul", value: [
        "une séance complète lorsque vous avez suffisamment de temps ;",
        "une séance courte de 15 à 20 minutes lorsque votre journée est chargée.",
      ]},
      { type: "p", value: "Cette stratégie évite de tomber dans une logique du « tout ou rien »." },
      { type: "p", value: "Une petite séance permet de conserver la routine et peut être plus utile qu'une séance parfaite constamment repoussée." },
      { type: "h", value: "Construisez progressivement une habitude" },
      { type: "p", value: "Essayez de réaliser votre séance dans un contexte relativement stable : au même horaire, dans le même lieu ou au même moment de votre journée." },
      { type: "p", value: [
        "La méta-analyse de ",
        { link: "Ma et al. (2023)", href: REF.ma2023 },
        " montre que les stratégies fondées sur la répétition, l'organisation de l'environnement et l'anticipation des obstacles peuvent renforcer les habitudes liées à l'activité physique.",
      ]},
      { type: "p", value: "Vous pouvez, par exemple :" },
      { type: "ul", value: [
        "préparer votre tenue à l'avance ;",
        "laisser votre matériel visible ;",
        "associer votre séance à un moment précis de la journée ;",
        "noter vos entraînements dans votre agenda ;",
        "réduire le nombre de décisions nécessaires avant de commencer.",
      ]},
      { type: "h", value: "Suivez plusieurs formes de progrès" },
      { type: "p", value: "Le poids n'est pas le seul indicateur intéressant." },
      { type: "p", value: "Vous pouvez également observer :" },
      { type: "ul", value: [
        "une meilleure endurance ;",
        "une augmentation de votre force ;",
        "moins d'essoufflement ;",
        "une meilleure mobilité ;",
        "une meilleure qualité de sommeil ;",
        "davantage d'énergie ;",
        "une plus grande confiance dans vos capacités.",
      ]},
      { type: "p", value: "Notez vos séances et vos sensations afin de rendre ces progrès visibles." },
      { type: "h", value: "Une séance manquée ne signifie pas un échec" },
      { type: "p", value: "Une semaine imparfaite ne détruit pas les progrès déjà réalisés." },
      { type: "p", value: "Évitez de vouloir rattraper une séance manquée en doublant brutalement le volume de la suivante." },
      { type: "p", value: "Reprenez simplement votre programme habituel dès que possible." },
      { type: "p", value: "La réussite ne dépend pas d'une régularité parfaite, mais de votre capacité à reprendre après les périodes plus difficiles." },
      { type: "h", value: "À retenir" },
      { type: "p", value: "Pour rester régulier :" },
      { type: "ul", value: [
        "choisissez une activité qui vous convient ;",
        "fixez-vous des objectifs mesurables ;",
        "planifiez le jour, l'heure et le lieu ;",
        "prévoyez une version courte de vos séances ;",
        "suivez vos progrès ;",
        "reprenez normalement après une séance manquée ;",
        "construisez progressivement une habitude.",
      ]},
      { type: "p", value: "Vous n'avez pas besoin d'être extrêmement motivé tous les jours." },
      { type: "p", value: "Vous avez surtout besoin d'une organisation suffisamment simple pour continuer." },
    ],
  },
];

// ------- ENGLISH ARTICLES -------
const ARTICLES_EN = [
  {
    id: "return-to-exercise",
    slug: "how-to-get-back-into-exercise",
    title: "How to Get Back into Exercise After a Long Break",
    excerpt: "Learn how to return to exercise gradually, avoid doing too much too soon and build a routine you can maintain.",
    category: "Return",
    readMin: 5,
    cover: COVERS.reprise,
    blocks: [
      { type: "p", value: "Getting back into exercise after several months or years away can feel difficult. However, you do not need to be in excellent physical condition before you start again." },
      { type: "p", value: "The most important thing is not to complete a perfect workout. It is to gradually rebuild the habit of moving regularly." },
      { type: "h", value: "Start Gradually" },
      { type: "p", value: "One of the most common mistakes is trying to immediately return to the fitness level you had before your break." },
      { type: "p", value: "Your body needs time to readapt to physical effort, impact and strength training." },
      { type: "p", value: "During your first few weeks, choose simple activities such as:" },
      { type: "ul", value: [
        "brisk walking;",
        "moderate-intensity cycling;",
        "bodyweight strength exercises;",
        "mobility exercises;",
        "short circuit-training sessions.",
      ]},
      { type: "p", value: "A 20- to 30-minute workout can already be a very good starting point." },
      { type: "p", value: "You can then gradually increase the duration and intensity according to how you feel." },
      { type: "p", value: "The World Health Organization states that any amount of physical activity is better than none." },
      { type: "p", value: "You therefore do not need to immediately reach a high training volume to start experiencing benefits." },
      { type: "p", value: [
        "According to ",
        { link: "Bull et al. (2020)", href: REF.bull2020 },
        ", inactive people should begin with small amounts of physical activity before gradually increasing the frequency, intensity and duration.",
      ]},
      { type: "p", value: [
        "The American College of Sports Medicine also recommends adapting an exercise programme to a person's usual activity level, physical condition, health status and individual goals. ",
        { link: "Garber et al. (2011)", href: REF.garber2011 },
        " particularly emphasise the importance of individualised progression.",
      ]},
      { type: "h", value: "Set a Realistic First Goal" },
      { type: "p", value: "Your first goal does not necessarily need to be losing several kilograms or running continuously for an hour." },
      { type: "p", value: "It could simply be to:" },
      { type: "ul", value: [
        "complete two workouts per week;",
        "walk more during the day;",
        "finish a workout without feeling completely exhausted;",
        "maintain your routine for one month.",
      ]},
      { type: "p", value: "An achievable goal allows you to experience an early success and gradually build confidence." },
      { type: "h", value: "Learn the Difference Between Muscle Soreness and Pain" },
      { type: "p", value: "After an unfamiliar physical activity, muscle soreness may appear during the following hours." },
      { type: "p", value: "It is often more noticeable one or two days after the workout, particularly after new exercises or movements involving a large eccentric component." },
      { type: "p", value: [
        "This phenomenon, known as delayed-onset muscle soreness, is described by ",
        { link: "Cheung, Hume and Maxwell (2003)", href: REF.cheung2003 },
        ".",
      ]},
      { type: "p", value: "However, muscle soreness is not necessary for a workout to be effective." },
      { type: "p", value: "Sharp, localised, unusual or persistent pain should not be treated as ordinary muscle soreness." },
      { type: "p", value: "Stop the exercise concerned and seek advice from a healthcare professional if the pain continues." },
      { type: "h", value: "Choose an Activity You Enjoy" },
      { type: "p", value: "The best activity is not necessarily the one that burns the most calories." },
      { type: "p", value: "It is the one you are most likely to practise consistently." },
      { type: "p", value: "You may choose strength training, walking, swimming, cycling, group classes or outdoor training." },
      { type: "p", value: "Enjoyment and ease of organisation are essential if you want to remain active over time." },
      { type: "h", value: "Key Takeaways" },
      { type: "p", value: "To return to exercise sustainably:" },
      { type: "ul", value: [
        "begin at an appropriate intensity;",
        "prioritise one or two regular workouts rather than one excessive week;",
        "gradually increase the duration and difficulty;",
        "allow your body enough time to recover;",
        "adapt exercises if you experience pain or physical limitations;",
        "choose an activity that fits your daily routine.",
      ]},
      { type: "p", value: "The aim of the first few weeks is not to break records." },
      { type: "p", value: "It is to create a routine you can maintain." },
    ],
  },
  {
    id: "weekly-training-frequency",
    slug: "how-many-times-a-week-should-you-exercise",
    title: "How Many Times a Week Should You Exercise to Make Progress?",
    excerpt: "Discover how many weekly workouts a beginner really needs and why consistency matters more than exercising every day.",
    category: "Method",
    readMin: 4,
    cover: COVERS.frequence,
    blocks: [
      { type: "p", value: "When starting out, many people believe they need to exercise almost every day to see results." },
      { type: "p", value: "In reality, the best training frequency is one that allows you to progress while remaining compatible with your schedule and your ability to recover." },
      { type: "h", value: "Two Workouts per Week Are a Strong Starting Point" },
      { type: "p", value: "For a beginner, two structured workouts per week can already improve strength, endurance and general fitness." },
      { type: "p", value: "This frequency also provides several recovery days between sessions." },
      { type: "p", value: "It is usually easier to maintain than a programme containing five or six weekly workouts." },
      { type: "p", value: "For example, you could begin with two full-body sessions combining:" },
      { type: "ul", value: [
        "lower-body exercises;",
        "upper-body movements;",
        "core-strengthening exercises;",
        "a few minutes of cardiovascular work.",
      ]},
      { type: "p", value: [
        "The American College of Sports Medicine recommends training the major muscle groups approximately two to three days per week. These recommendations are presented by ",
        { link: "Garber et al. (2011)", href: REF.garber2011 },
        ".",
      ]},
      { type: "p", value: "The World Health Organization also recommends that adults perform muscle-strengthening activities at least two days per week, in addition to aerobic activity." },
      { type: "p", value: [
        "However, these recommendations represent a general target. Someone who is currently inactive can begin with a smaller amount and progress gradually, as explained by ",
        { link: "Bull et al. (2020)", href: REF.bull2020 },
        ".",
      ]},
      { type: "h", value: "Three Workouts Allow for Greater Variety" },
      { type: "p", value: "Once your body has adapted to regular exercise, you may decide to increase your frequency to three sessions per week." },
      { type: "p", value: "A simple weekly structure could include:" },
      { type: "ul", value: [
        "one strength-training session;",
        "one cardiovascular session;",
        "one session combining strength, mobility and cardio.",
      ]},
      { type: "p", value: "You could also perform three shorter full-body workouts if this structure fits your schedule more easily." },
      { type: "h", value: "Frequency Is Not the Only Important Factor" },
      { type: "p", value: "Completing more workouts does not automatically guarantee better results." },
      { type: "p", value: "Total training volume, exercise difficulty, the number of sets performed, recovery and long-term consistency are also important." },
      { type: "p", value: [
        "The meta-analysis conducted by ",
        { link: "Schoenfeld, Grgic and Krieger (2019)", href: REF.schoenfeld2019 },
        " indicates that when total training volume is similar, simply increasing training frequency does not necessarily produce substantially greater muscle growth.",
      ]},
      { type: "p", value: "Training frequency is therefore mainly a way of distributing your weekly workload effectively." },
      { type: "h", value: "Do Not Neglect Recovery" },
      { type: "p", value: "The adaptations caused by training also take place during recovery." },
      { type: "p", value: "An effective programme should therefore include:" },
      { type: "ul", value: [
        "lighter days or days without intense exercise;",
        "sufficient sleep;",
        "an appropriate diet;",
        "a reasonable progression;",
        "adjustments when unusual fatigue occurs.",
      ]},
      { type: "h", value: "Key Takeaways" },
      { type: "p", value: "For a beginner:" },
      { type: "ul", value: [
        "one weekly workout can already help you become active again;",
        "two workouts provide a strong foundation;",
        "three workouts allow for greater variety;",
        "you do not need to train every day;",
        "consistency is more important than choosing a frequency you cannot maintain.",
      ]},
      { type: "p", value: "Two workouts completed every week for several months will usually be more beneficial than five workouts completed for only ten days." },
    ],
  },
  {
    id: "cardio-strength-weight-loss",
    slug: "cardio-or-strength-training-for-weight-loss",
    title: "Should You Do Cardio or Strength Training to Lose Weight?",
    excerpt: "Understand the complementary roles of cardio and strength training in fat loss and body composition.",
    category: "Fat loss",
    readMin: 5,
    cover: COVERS.cardio,
    blocks: [
      { type: "p", value: "Many people believe they need to choose between cardio and strength training when trying to lose weight." },
      { type: "p", value: "In practice, these two forms of exercise play different roles, but they can work very well together." },
      { type: "h", value: "Cardio Increases Energy Expenditure" },
      { type: "p", value: "Brisk walking, cycling, swimming, running and circuit training all increase energy expenditure during physical activity." },
      { type: "p", value: "You do not have to run." },
      { type: "p", value: "For a beginner, brisk walking can already be an effective and accessible option that is easier to repeat several times per week." },
      { type: "p", value: "You can begin with sessions lasting 20 to 30 minutes before gradually increasing the duration or intensity." },
      { type: "h", value: "Strength Training Helps Preserve Muscle Mass" },
      { type: "p", value: "During weight loss, the objective should not only be to reduce the number displayed on the scales." },
      { type: "p", value: "It is also important to preserve as much muscle mass as possible." },
      { type: "p", value: "Strength training improves physical strength and helps maintain or develop muscle tissue." },
      { type: "p", value: "A workout can be performed using machines, dumbbells, resistance bands or simply your body weight." },
      { type: "p", value: [
        "The meta-analysis conducted by ",
        { link: "Lopez et al. (2022)", href: REF.lopez2022 },
        " shows that resistance training can improve body composition in people with overweight or obesity, particularly by supporting fat-mass reduction and the maintenance or development of lean mass.",
      ]},
      { type: "h", value: "Why Combine Cardio and Strength Training?" },
      { type: "p", value: "An analysis bringing together 12 systematic reviews and 149 studies found that physical exercise can improve body weight and body composition in adults with overweight or obesity." },
      { type: "p", value: [
        "According to ",
        { link: "Bellicha et al. (2021)", href: REF.bellicha2021 },
        ", aerobic exercise is particularly useful for increasing energy expenditure and reducing fat mass.",
      ]},
      { type: "p", value: "Strength training provides an additional benefit by helping to maintain lean mass." },
      { type: "p", value: "A simple weekly routine could therefore include:" },
      { type: "ul", value: [
        "one or two strength-training sessions;",
        "one structured cardio session;",
        "walking or light physical activity on the other days.",
      ]},
      { type: "h", value: "Nutrition Remains Important" },
      { type: "p", value: "Physical activity contributes to energy expenditure, but changes in body weight are also influenced by nutrition, sleep, recovery and overall daily activity levels." },
      { type: "p", value: "This does not mean that you need to follow an extremely restrictive diet." },
      { type: "p", value: "A gradual approach is generally easier to maintain:" },
      { type: "ul", value: [
        "choose appropriate portion sizes;",
        "regularly include fruit and vegetables;",
        "consume enough protein;",
        "limit high-calorie drinks;",
        "avoid automatically compensating for every workout by overeating afterwards.",
      ]},
      { type: "h", value: "Do Not Rely Only on the Scales" },
      { type: "p", value: "It is possible to lose body fat without immediately seeing a large reduction in body weight, especially when muscle mass is preserved or developed." },
      { type: "p", value: "You can also monitor:" },
      { type: "ul", value: [
        "your waist circumference;",
        "how your clothes fit;",
        "your strength;",
        "your endurance;",
        "your energy levels;",
        "photographs taken under similar conditions.",
      ]},
      { type: "h", value: "Key Takeaways" },
      { type: "p", value: "To reduce body fat sustainably:" },
      { type: "ul", value: [
        "cardio increases energy expenditure;",
        "strength training helps preserve or develop muscle;",
        "combining the two provides a more complete approach;",
        "nutrition also influences weight management;",
        "consistency is more important than extreme workouts.",
      ]},
      { type: "p", value: "You do not need to choose permanently between cardio and strength training." },
    ],
  },
  {
    id: "nutrition-before-after-workout",
    slug: "what-to-eat-before-and-after-a-workout",
    title: "What Should You Eat Before and After a Workout?",
    excerpt: "Learn what to eat before and after exercise without making sports nutrition unnecessarily complicated.",
    category: "Nutrition",
    readMin: 6,
    cover: COVERS.nutrition,
    blocks: [
      { type: "p", value: "Nutrition can influence how comfortable you feel during exercise and how well you recover afterwards." },
      { type: "p", value: "However, a beginner does not need to follow a complicated nutrition strategy." },
      { type: "p", value: "The main priority is to maintain a sufficiently varied and balanced diet throughout the day." },
      { type: "h", value: "What Should You Eat Before Training?" },
      { type: "p", value: "Your pre-workout meal should provide energy without causing digestive discomfort." },
      { type: "p", value: "If you eat approximately two to three hours before training, you could choose a regular meal containing:" },
      { type: "ul", value: [
        "a source of carbohydrates, such as rice, bread, pasta or potatoes;",
        "a source of protein, such as eggs, fish, meat, dairy products or legumes;",
        "vegetables;",
        "water.",
      ]},
      { type: "p", value: "If your last meal was several hours ago and you feel hungry, a small snack may be enough:" },
      { type: "ul", value: [
        "a piece of fruit;",
        "fruit purée or apple sauce;",
        "yoghurt;",
        "a slice of bread;",
        "a small portion of simple cereal.",
      ]},
      { type: "p", value: "Avoid trying a very large, high-fat or unfamiliar meal immediately before an important workout." },
      { type: "p", value: [
        "The position statement published by ",
        { link: "Thomas, Erdman and Burke (2016)", href: REF.thomas2016 },
        " explains that nutrition should be adapted to the type, duration and intensity of the activity, as well as to each person's digestive tolerance.",
      ]},
      { type: "p", value: "Carbohydrates play an important role during prolonged or high-intensity exercise." },
      { type: "p", value: "However, the needs of a beginner completing a 45-minute fitness session are different from those of an endurance athlete." },
      { type: "h", value: "What Should You Eat After Training?" },
      { type: "p", value: "After exercise, a balanced meal can provide your body with the nutrients required for recovery." },
      { type: "p", value: "It may include:" },
      { type: "ul", value: [
        "a source of protein;",
        "a source of carbohydrates;",
        "vegetables or fruit;",
        "water.",
      ]},
      { type: "p", value: "For example:" },
      { type: "ul", value: [
        "chicken, rice and vegetables;",
        "an omelette, bread and salad;",
        "fish, potatoes and vegetables;",
        "yoghurt, fruit and oats as a snack.",
      ]},
      { type: "h", value: "Is a Protein Shake Essential?" },
      { type: "p", value: "No." },
      { type: "p", value: "A dietary supplement is not essential when your nutritional needs are already covered by food." },
      { type: "p", value: "Protein contributes to muscle repair and adaptation, but results depend mainly on your overall daily intake and how it is distributed throughout the day." },
      { type: "p", value: [
        "The position stand published by ",
        { link: "Kerksick et al. (2017)", href: REF.kerksick2017 },
        " indicates that consuming protein around a workout can support recovery.",
      ]},
      { type: "p", value: "However, there is no need to panic if you do not drink a protein shake immediately after your final repetition." },
      { type: "p", value: "For a beginner, a varied diet that regularly includes eggs, fish, meat, dairy products, soy or legumes can usually provide the required protein." },
      { type: "h", value: "Do Not Forget Hydration" },
      { type: "p", value: "Your fluid requirements depend on factors such as:" },
      { type: "ul", value: [
        "the duration of the workout;",
        "the temperature;",
        "how much you sweat;",
        "exercise intensity;",
        "individual characteristics.",
      ]},
      { type: "p", value: "For a relatively short and standard workout, water is usually an appropriate choice." },
      { type: "p", value: "When exercise lasts longer, takes place in hot conditions or causes heavy sweating, fluid and electrolyte requirements may increase." },
      { type: "p", value: [
        "The recommendations provided by ",
        { link: "Sawka et al. (2007)", href: REF.sawka2007 },
        " emphasise that hydration should be individualised according to sweat losses and exercise conditions.",
      ]},
      { type: "p", value: "The objective is not to drink as much as possible." },
      { type: "p", value: "It is to begin the workout properly hydrated and reasonably replace the fluids you lose." },
      { type: "h", value: "Key Takeaways" },
      { type: "p", value: "Before and after exercise:" },
      { type: "ul", value: [
        "choose simple foods that you digest comfortably;",
        "adjust the amount according to how soon you will be exercising;",
        "consume protein and carbohydrates regularly;",
        "stay hydrated throughout the day;",
        "do not make sports nutrition more complicated than necessary;",
        "do not consider dietary supplements essential.",
      ]},
      { type: "p", value: "The overall quality of your diet is more important than consuming one specific food at one precise moment." },
    ],
  },
  {
    id: "exercise-motivation",
    slug: "how-to-stay-motivated-when-starting-exercise",
    title: "How to Stay Motivated When You Start Exercising",
    excerpt: "Discover simple, science-based strategies to remain consistent even when your motivation decreases.",
    category: "Motivation",
    readMin: 5,
    cover: COVERS.motivation,
    blocks: [
      { type: "p", value: "Motivation is often high at the beginning of a new programme." },
      { type: "p", value: "However, it can decrease when fatigue appears, your schedule becomes busy or your results take longer than expected." },
      { type: "p", value: "To exercise consistently, it is better to create a routine that still works when your motivation is lower." },
      { type: "h", value: "Choose a Specific Goal" },
      { type: "p", value: "A goal such as \u201cgetting fitter\u201d is positive, but it can be difficult to measure." },
      { type: "p", value: "Turn it into a specific action, such as:" },
      { type: "ul", value: [
        "completing two workouts per week;",
        "walking for 30 minutes three times per week;",
        "completing ten modified push-ups;",
        "running five kilometres without stopping;",
        "gradually increasing the weights you use;",
        "improving your daily energy levels.",
      ]},
      { type: "p", value: "A specific goal makes it easier to understand exactly what you are working towards." },
      { type: "h", value: "Choose an Activity That Matters to You" },
      { type: "p", value: "Some people exercise only because they believe they are supposed to." },
      { type: "p", value: "This type of motivation may work temporarily, but it is often difficult to maintain." },
      { type: "p", value: [
        "The systematic review conducted by ",
        { link: "Teixeira et al. (2012)", href: REF.teixeira2012 },
        " shows that more autonomous forms of motivation are more strongly associated with long-term participation in physical activity.",
      ]},
      { type: "p", value: "In other words, you are more likely to remain consistent when you understand why you are exercising, when the activity matches your preferences and when you have some freedom in how you practise it." },
      { type: "h", value: "Plan Your Workouts Precisely" },
      { type: "p", value: "Writing \u201cexercise this week\u201d is too vague." },
      { type: "p", value: "It is more effective to plan something specific:" },
      { type: "p", value: "On Tuesday at 6:00 p.m., I will complete a 30-minute workout at home." },
      { type: "p", value: "This type of plan specifies the time, place and action." },
      { type: "p", value: [
        "The meta-analysis conducted by ",
        { link: "Silva et al. (2018)", href: REF.silva2018 },
        " shows that specific action plans, sometimes known as implementation intentions, can help turn an intention into an actual behaviour.",
      ]},
      { type: "h", value: "Prepare an Alternative for Difficult Days" },
      { type: "p", value: "You can prepare two versions of each workout:" },
      { type: "ul", value: [
        "a complete workout when you have enough time;",
        "a shorter 15- to 20-minute workout when your day is busy.",
      ]},
      { type: "p", value: "This strategy helps you avoid an all-or-nothing mindset." },
      { type: "p", value: "A short workout can help you maintain your routine and may be more useful than a perfect workout that is repeatedly postponed." },
      { type: "h", value: "Build a Habit Gradually" },
      { type: "p", value: "Try to exercise in a relatively stable context, such as at the same time, in the same place or at the same point in your daily routine." },
      { type: "p", value: [
        "The meta-analysis conducted by ",
        { link: "Ma et al. (2023)", href: REF.ma2023 },
        " shows that strategies based on repetition, environmental organisation and anticipating potential obstacles can strengthen physical-activity habits.",
      ]},
      { type: "p", value: "For example, you can:" },
      { type: "ul", value: [
        "prepare your workout clothes in advance;",
        "keep your equipment visible;",
        "connect your workout to a specific moment in your day;",
        "schedule your sessions in your calendar;",
        "reduce the number of decisions you need to make before starting.",
      ]},
      { type: "h", value: "Monitor Different Forms of Progress" },
      { type: "p", value: "Body weight is not the only useful indicator." },
      { type: "p", value: "You can also monitor:" },
      { type: "ul", value: [
        "improved endurance;",
        "increased strength;",
        "reduced breathlessness;",
        "improved mobility;",
        "better sleep quality;",
        "higher energy levels;",
        "greater confidence in your physical abilities.",
      ]},
      { type: "p", value: "Record your workouts and how you feel in order to make these improvements more visible." },
      { type: "h", value: "Missing One Workout Does Not Mean You Have Failed" },
      { type: "p", value: "One imperfect week does not erase the progress you have already made." },
      { type: "p", value: "Avoid trying to compensate for a missed workout by suddenly doubling the volume of your next session." },
      { type: "p", value: "Simply return to your usual programme as soon as possible." },
      { type: "p", value: "Success does not depend on perfect consistency." },
      { type: "p", value: "It depends on your ability to start again after more difficult periods." },
      { type: "h", value: "Key Takeaways" },
      { type: "p", value: "To remain consistent:" },
      { type: "ul", value: [
        "choose an activity that suits you;",
        "set measurable goals;",
        "plan the day, time and location;",
        "prepare a shorter version of your workouts;",
        "monitor your progress;",
        "return to your normal routine after a missed workout;",
        "gradually build an exercise habit.",
      ]},
      { type: "p", value: "You do not need to feel extremely motivated every day." },
      { type: "p", value: "You mainly need an organisation that is simple enough to maintain." },
    ],
  },
];

// ------- BLOG EXPORTS -------
export const BLOG_FR = {
  label: "Blog",
  heroLines: ["Conseils & méthode", "pour progresser."],
  sub: "Des articles pratiques pour reprendre le sport, structurer vos séances et rester régulier — sans jargon, sans promesses irréalistes.",
  seoTitle: "Blog | Gihed Coaching · Conseils sport & entraînement",
  seoDesc: "Conseils pratiques d'un coach sportif à Nice : reprise du sport, fréquence des séances, nutrition, cardio, musculation et motivation.",
  articles: ARTICLES_FR,
};

export const BLOG_EN = {
  label: "Blog",
  heroLines: ["Advice & method", "to keep progressing."],
  sub: "Practical articles to help you get back into sport, structure your sessions and stay consistent — no jargon, no unrealistic promises.",
  seoTitle: "Blog | Gihed Coaching · Sport & training advice",
  seoDesc: "Practical advice from a personal trainer in Nice: getting back into sport, session frequency, nutrition, cardio, strength and motivation.",
  articles: ARTICLES_EN,
};

// Helper: find an article by slug in either language.
// Returns { article, lang } if found, else null.
export const findArticleBySlug = (slug) => {
  const fr = ARTICLES_FR.find((a) => a.slug === slug);
  if (fr) return { article: fr, lang: "fr" };
  const en = ARTICLES_EN.find((a) => a.slug === slug);
  if (en) return { article: en, lang: "en" };
  return null;
};

// Helper: get the article slug in the target language from a shared id.
export const getSlugByIdAndLang = (id, lang) => {
  const set = lang === "en" ? ARTICLES_EN : ARTICLES_FR;
  const found = set.find((a) => a.id === id);
  return found ? found.slug : null;
};
